import {resource} from '@albert-io/json-api-framework/request/builder';
import {chunk} from 'lodash';
import {genericMandarkRequest} from 'resources/mandark.resource';
import {List} from 'immutable';
import {AlternateContentLanguage, TranslatedSupplement} from './Translation.types';

export const fetchTranslatedQuestion = async (
  questionId: string,
  language: AlternateContentLanguage
) => {
  const response = await genericMandarkRequest('get', {
    resourcePath: ['json', 'translations', 'translated_questions', questionId],
    customQuery: {language, include: 'translated_fields'}
  });

  return {info: response.toJS()}; // Map(response);
};

export const fetchTranslatedSupplements = async (supplementIds: string[]) => {
  const chunkSize = 10;
  const chunks = chunk(supplementIds, chunkSize);

  const supplementsPromises: any[] = [];
  const translatedSupplements: TranslatedSupplement[] = [];

  for (const supplementChunk of chunks) {
    supplementsPromises.push(
      ...supplementChunk.map((id) =>
        genericMandarkRequest('get', {
          resourcePath: ['json', 'translations', 'translated_supplements', id],
          customQuery: {language: 'es', include: 'translated_fields'}
        })
      )
    );
  }

  const responses = await Promise.all(supplementsPromises);

  translatedSupplements.push(
    ...responses.map((response) => {
      return response.toJS();
    })
  );

  return translatedSupplements;
};

export const fetchQuestionSupplements = async (questionId: string) => {
  const query = resource('supplement_v1').mandarkEndpoint([
    'questions_v3',
    questionId,
    'supplements_v1'
  ]);

  try {
    const response = await query.getResourcePromise();
    return response;
  } catch (_err) {
    return List([]);
  }
};

// Queries for Assignment settings translation status

export const getStudentClassWithSchool = async (
  assignmentId: string | null,
  studentId: string | null
) => {
  if (!assignmentId || !studentId) return null;
  const query = resource('assignments_v3')
    .mandarkEndpoint(['assignments_v3', assignmentId])
    .include('classrooms_v1.school_v5')
    .filter({
      included: {
        classrooms_v1: {
          status: 'active',
          students_v2: {
            id: studentId
          }
        }
      }
    });

  try {
    const response = await query.getResourcePromise();
    return response.getClassrooms().first();
    // const school = response.getClassrooms().first()?.getSchool();
    // return school;
  } catch (_err) {
    return null;
  }
};

export const getStudentSchoolPermissionsWithoutAssignment = async (studentId: string | null) => {
  if (!studentId) return false;
  const query = resource('school_v5')
    .mandarkEndpoint(['students_v2', studentId, 'classrooms_v1'])
    .filter({
      status: 'active'
    })
    .include('school_v5');
  try {
    const response = await query.getResourcePromise();
    return response.some((classroom) => {
      const school = classroom.getSchool();
      return school?.isAllowTranslations() || false;
    });
  } catch (_err) {
    return false;
  }
};

export const getTeacherTranslationPermissions = async (teacherId: string) => {
  const query = getTeacherWithMetadataQuery(teacherId);
  try {
    const teacher = await query.getResourcePromise();
    const schoolLicensesInfo = teacher.getMeta().getSchoolLicensesInfo();
    const schoolsPromises = schoolLicensesInfo
      .filter((licenseInformation) => {
        const hasSchoolLicense = licenseInformation.get('has_school_license');
        const licenseStatus = licenseInformation.get('school_license_status');
        return hasSchoolLicense && licenseStatus === 'active';
      })
      .map((approvedlicenseInformation) =>
        getSchoolByIdQuery(approvedlicenseInformation.get('school_id'))
      );
    const schools = await Promise.all(schoolsPromises);
    return schools.some((school) => school?.isAllowTranslations() || false);
  } catch (_err) {
    return false;
  }
};

// imported from marketplace/src/client/AccountSettings/Subscriptions/InstructionalLicenseCard.js
const getSchoolByIdQuery = (id) => {
  return resource('school_v5').mandarkEndpoint(['schools_v5', id]).getResourcePromise();
};

const getTeacherWithMetadataQuery = (teacherId) => {
  return resource('teachers_v1')
    .mandarkEndpoint(['teachers_v1', teacherId])
    .include('school_v5')
    .include('school_personnel_v1')
    .include('school_personnel_v1.school_v5')
    .withMeta('teacher_v1')
    .customQuery({
      meta: {
        context: {
          teacher: {
            license_info: true
          }
        }
      }
    });
};

export const getAssignmentQuery = (assignmentId, classId, studentId) => {
  const hasClass = classId !== null && classId !== undefined;

  const filter: any = {
    included: {
      students_v2: {
        id: studentId
      },
      ...(hasClass && {classrooms_v1: {id: classId}})
    }
  };

  const baseQuery = resource('assignments_v3')
    .mandarkEndpoint(['assignments_v3', assignmentId])
    .include('students_v2');

  const query = hasClass ? baseQuery.include('classrooms_v1') : baseQuery;

  return query
    .filter(filter)
    .meta({
      context: {
        student: {
          id: studentId
        }
      }
    })
    .withMeta('assignment_v3');
};
